import { getReturnIdsFromDatasetInstruction } from 'helpers/dataset.helper/dataset.helper'
import DEFAULT_DATASET_TEMPLATES from 'ui/uiConfig/template/datasets'
import type {
  DataFlowCalcDependency,
  DataFlowConsistencySummary,
  DataFlowEdge,
  DataFlowGraph,
  DataFlowSource,
  DataFlowWarning,
  DataFlowWarningSourceHint,
} from './UiConfigDataFlowPanel.types'

// ─── Internal type guards ─────────────────────────────────────────────────────

function getDsiReturnIds(dsi: Partial<DatasetInstruction>): string[] {
  try {
    const returnIds = getReturnIdsFromDatasetInstruction(dsi)
    const normalizedReturnIds = returnIds.filter(
      (returnId: unknown): returnId is string => typeof returnId === 'string'
    )

    if (normalizedReturnIds.length > 0) {
      return normalizedReturnIds
    }
  } catch {
    // Keep the inspector resilient for partial draft DSIs while still preferring
    // the shared helper when the instruction shape is complete.
  }

  const explicitReturnId = (dsi as Record<string, unknown>).return_id
  return typeof explicitReturnId === 'string' ? [explicitReturnId] : []
}

function getDsiType(dsi: Partial<DatasetInstruction>): string | null {
  const type = (dsi as Record<string, unknown>).type
  return typeof type === 'string' ? type : null
}

function getCalcVariables(dsi: Partial<DatasetInstruction>): (string | number)[] {
  const contract = (dsi as Record<string, unknown>).contract
  if (!contract || typeof contract !== 'object') return []
  const variables = (contract as Record<string, unknown>).variables
  if (!Array.isArray(variables)) return []
  return variables.filter((v): v is string | number => typeof v === 'string' || typeof v === 'number')
}

function getDatasetInstructionsForSource(uiConfig: UiConfig | null | undefined): Partial<DatasetInstruction>[] {
  if (!uiConfig) {
    return []
  }

  return [...(uiConfig.dataset_instructions ?? []), ...(uiConfig.props?.dataset_instructions ?? [])]
}

function getReturnIdsSet(datasetInstructions: Partial<DatasetInstruction>[]): Set<string> {
  return new Set(datasetInstructions.flatMap((dsi) => getDsiReturnIds(dsi)))
}

function getDataBearingItems(uiConfig: UiConfig): UiConfigPropsItem[] {
  const items = uiConfig.props?.items ?? []

  return items.filter((item): item is UiConfigPropsItem & { data_id: string } => typeof item?.data_id === 'string')
}

function getTemplateSourceHint(returnId: string | null, baseUiConfig?: UiConfig | null): DataFlowWarningSourceHint {
  if (!returnId) {
    return 'incomplete'
  }

  const baseTemplateSourceDsis = baseUiConfig?.props?.dataset_instructions ?? []
  const baseReturnIds = getReturnIdsSet(baseTemplateSourceDsis)
  if (baseReturnIds.has(returnId)) {
    return 'base_config'
  }

  if (Object.hasOwn(DEFAULT_DATASET_TEMPLATES, returnId)) {
    return 'code_template'
  }

  return 'unknown'
}

function isDraftLikeCalcVariable(variable: string): boolean {
  return variable.trim().length === 0 || variable.includes('$') || variable.includes('{')
}

function resolveReturnIdSource(
  returnId: string,
  rawUiConfig?: UiConfig | null,
  baseUiConfig?: UiConfig | null,
  resolvedDsiType?: string | null
): DataFlowSource {
  if (resolvedDsiType === 'calc') {
    return 'calc_output'
  }

  const rawDatasetInstructions = getDatasetInstructionsForSource(rawUiConfig)
  const rawTemplateReturnIds = new Set(
    rawDatasetInstructions.filter((dsi) => getDsiType(dsi) === 'template').flatMap((dsi) => getDsiReturnIds(dsi))
  )
  if (rawTemplateReturnIds.has(returnId)) {
    return 'template_resolution'
  }

  const rawExplicitReturnIds = new Set(
    rawDatasetInstructions.filter((dsi) => getDsiType(dsi) !== 'template').flatMap((dsi) => getDsiReturnIds(dsi))
  )
  if (rawExplicitReturnIds.has(returnId)) {
    return 'raw_config'
  }

  const baseReturnIds = getReturnIdsSet(getDatasetInstructionsForSource(baseUiConfig))
  if (baseReturnIds.has(returnId)) {
    return 'base_config'
  }

  return 'unknown'
}

// ─── Public helpers ───────────────────────────────────────────────────────────

/**
 * Extracts the data-flow edges from a fully resolved UiConfig.
 *
 * Each edge represents one of:
 * - `matched`: an item whose data_id lines up with a DSI return_id.
 * - `unmatched_item`: an item whose data_id has no corresponding DSI.
 * - `orphaned_dsi`: a DSI whose return_id is not consumed by any item.
 *
 * DSIs without a return_id (e.g. anonymous templates) are excluded from edge
 * results; they surface only as warnings via detectDataFlowWarnings.
 */
export function extractDataFlowEdges(
  resolvedUiConfig: UiConfig,
  options: { rawUiConfig?: UiConfig | null; baseUiConfig?: UiConfig | null } = {}
): DataFlowEdge[] {
  const { rawUiConfig = null, baseUiConfig = null } = options
  const items = getDataBearingItems(resolvedUiConfig)
  const dsis = getDatasetInstructionsForSource(resolvedUiConfig)
  const indexedDsis = dsis.flatMap((dsi) =>
    getDsiReturnIds(dsi).map((returnId) => ({
      returnId,
      dsi,
    }))
  )

  const dsiByReturnId = new Map<string, (typeof indexedDsis)[number]>()
  for (const entry of indexedDsis) {
    if (!dsiByReturnId.has(entry.returnId)) {
      dsiByReturnId.set(entry.returnId, entry)
    }
  }

  const edges: DataFlowEdge[] = []
  const matchedDsiReturnIds = new Set<string>()

  for (const item of items) {
    const dataId = item.data_id
    const title = item.title
    const matchingDsi = dataId != null ? dsiByReturnId.get(dataId) : undefined

    if (matchingDsi) {
      const returnId = matchingDsi.returnId
      matchedDsiReturnIds.add(returnId)
      edges.push({
        itemDataId: dataId,
        itemTitle: title,
        dsiReturnId: returnId,
        dsiType: getDsiType(matchingDsi.dsi),
        status: 'matched',
        source: resolveReturnIdSource(returnId, rawUiConfig, baseUiConfig, getDsiType(matchingDsi.dsi)),
      })
    } else {
      edges.push({
        itemDataId: dataId,
        itemTitle: title,
        dsiReturnId: null,
        dsiType: null,
        status: 'unmatched_item',
        source: null,
      })
    }
  }

  for (const { returnId, dsi } of indexedDsis) {
    if (!matchedDsiReturnIds.has(returnId)) {
      edges.push({
        itemDataId: null,
        itemTitle: null,
        dsiReturnId: returnId,
        dsiType: getDsiType(dsi),
        status: 'orphaned_dsi',
        source: resolveReturnIdSource(returnId, rawUiConfig, baseUiConfig, getDsiType(dsi)),
      })
    }
  }

  return edges
}

export function extractCalcDependencies(
  resolvedUiConfig: UiConfig,
  options: { rawUiConfig?: UiConfig | null; baseUiConfig?: UiConfig | null } = {}
): DataFlowCalcDependency[] {
  const { rawUiConfig = null, baseUiConfig = null } = options
  const dsis = getDatasetInstructionsForSource(resolvedUiConfig)
  const allReturnIds = new Set(dsis.flatMap((dsi) => getDsiReturnIds(dsi)))

  // Index the resolved DSI type by return_id so we can detect when a dependency
  // variable is itself produced by a calc instruction (fromSource = 'calc_output').
  const dsiTypeByReturnId = new Map<string, string | null>()
  for (const dsi of dsis) {
    for (const returnId of getDsiReturnIds(dsi)) {
      if (!dsiTypeByReturnId.has(returnId)) {
        dsiTypeByReturnId.set(returnId, getDsiType(dsi))
      }
    }
  }

  const dependencies: DataFlowCalcDependency[] = []

  for (const dsi of dsis) {
    if (getDsiType(dsi) !== 'calc') {
      continue
    }

    const calcReturnId = getDsiReturnIds(dsi)[0]
    if (!calcReturnId) {
      continue
    }

    for (const variable of getCalcVariables(dsi)) {
      if (typeof variable !== 'string') {
        continue
      }

      const variableDsiType = dsiTypeByReturnId.get(variable) ?? null
      dependencies.push({
        fromReturnId: variable,
        fromSource: resolveReturnIdSource(variable, rawUiConfig, baseUiConfig, variableDsiType),
        toReturnId: calcReturnId,
        status: allReturnIds.has(variable) ? 'resolved' : 'missing',
      })
    }
  }

  return dependencies
}

/**
 * Detects data-flow warnings from the edges and the resolved config.
 *
 * Warning kinds:
 * - `unmatched_item` (error): item has no backing DSI.
 * - `orphaned_dsi` (warning): DSI produces data no item consumes.
 * - `unresolved_template` (error): DSI still typed 'template' after full resolution.
 * - `calc_missing_variable` (warning): a calc DSI references a return_id not in the config.
 */
export function detectDataFlowWarnings(
  edges: DataFlowEdge[],
  resolvedUiConfig: UiConfig,
  options: { rawUiConfig?: UiConfig | null; baseUiConfig?: UiConfig | null } = {}
): DataFlowWarning[] {
  const warnings: DataFlowWarning[] = []
  const dsis = getDatasetInstructionsForSource(resolvedUiConfig)
  const { baseUiConfig = null } = options

  for (const edge of edges) {
    if (edge.status === 'unmatched_item') {
      warnings.push({
        kind: 'unmatched_item',
        severity: 'error',
        classification: 'runtime_failure',
        message: `Item "${edge.itemTitle}" has data_id "${edge.itemDataId}" but no dataset instruction with a matching return_id was found.`,
        context: {
          dataId: edge.itemDataId ?? undefined,
          itemTitle: edge.itemTitle ?? undefined,
        },
      })
    }
  }

  for (const edge of edges) {
    if (edge.status === 'orphaned_dsi') {
      warnings.push({
        kind: 'orphaned_dsi',
        severity: 'warning',
        classification: 'cleanup',
        message: `Dataset instruction with return_id "${edge.dsiReturnId}" is not consumed by any item.`,
        context: {
          returnId: edge.dsiReturnId ?? undefined,
          dsiType: edge.dsiType ?? undefined,
          sourceHint:
            edge.source === 'raw_config'
              ? 'raw_config'
              : edge.source === 'calc_output'
                ? 'raw_config'
                : edge.source === 'base_config'
                  ? 'base_config'
                  : edge.source === 'template_resolution'
                    ? 'template_resolution'
                    : 'unknown',
        },
      })
    }
  }

  for (const dsi of dsis) {
    if (getDsiType(dsi) === 'template') {
      const returnId = getDsiReturnIds(dsi)[0] ?? null
      const sourceHint = getTemplateSourceHint(returnId, baseUiConfig)
      warnings.push({
        kind: 'unresolved_template',
        severity: sourceHint === 'incomplete' ? 'info' : sourceHint === 'unknown' ? 'warning' : 'error',
        classification:
          sourceHint === 'incomplete' ? 'draft_state' : sourceHint === 'unknown' ? 'cleanup' : 'runtime_failure',
        message:
          sourceHint === 'incomplete'
            ? 'Template dataset instruction is incomplete while editing: return_id is still missing.'
            : sourceHint === 'base_config'
              ? `Template dataset instruction "${returnId}" should resolve from base_config but is still unresolved in the final config.`
              : sourceHint === 'code_template'
                ? `Template dataset instruction "${returnId}" matches a built-in code template but is still unresolved in the final config.`
                : `Template dataset instruction "${returnId}" has no matching source in base_config or built-in code templates.`,
        context: {
          returnId: returnId ?? undefined,
          dsiType: 'template',
          sourceHint,
        },
      })
    }
  }

  const allReturnIds = new Set(dsis.flatMap((dsi) => getDsiReturnIds(dsi)))

  for (const dsi of dsis) {
    if (getDsiType(dsi) !== 'calc') continue
    const returnId = getDsiReturnIds(dsi)[0] ?? '(unknown)'
    const variables = getCalcVariables(dsi)

    for (const variable of variables) {
      if (typeof variable === 'string' && !allReturnIds.has(variable)) {
        const isDraftLike = isDraftLikeCalcVariable(variable)
        warnings.push({
          kind: 'calc_missing_variable',
          severity: isDraftLike ? 'info' : 'warning',
          classification: isDraftLike ? 'draft_state' : 'cleanup',
          message: isDraftLike
            ? `Calc instruction with return_id "${returnId}" still contains an in-progress variable reference "${variable}".`
            : `Calc instruction with return_id "${returnId}" references variable "${variable}" which does not match any known return_id in this config.`,
          context: {
            returnId,
            variable,
            sourceHint: isDraftLike ? 'incomplete' : 'unknown',
          },
        })
      }
    }
  }

  return warnings
}

/**
 * Convenience entry point: builds the complete data-flow graph (edges + warnings)
 * from a fully resolved UiConfig in one call.
 */
export function buildDataFlowGraph(
  resolvedUiConfig: UiConfig,
  options: { rawUiConfig?: UiConfig | null; baseUiConfig?: UiConfig | null } = {}
): DataFlowGraph {
  const edges = extractDataFlowEdges(resolvedUiConfig, options)
  const calcDependencies = extractCalcDependencies(resolvedUiConfig, options)
  const warnings = detectDataFlowWarnings(edges, resolvedUiConfig, options)
  return { edges, calcDependencies, warnings }
}

export function buildDataFlowConsistencySummary(graph: DataFlowGraph): DataFlowConsistencySummary {
  const unresolvedItemsCount = graph.edges.filter((edge) => edge.status === 'unmatched_item').length
  const orphanedDsisCount = graph.edges.filter((edge) => edge.status === 'orphaned_dsi').length
  const unresolvedCalcVariablesCount = graph.warnings.filter(
    (warning) => warning.kind === 'calc_missing_variable' && warning.context.sourceHint !== 'incomplete'
  ).length

  const affectedOutputsCount = new Set(
    graph.warnings
      .map((warning) => warning.context.returnId ?? warning.context.dataId)
      .filter((value): value is string => typeof value === 'string' && value.length > 0)
  ).size

  return {
    unresolvedItemsCount,
    orphanedDsisCount,
    unresolvedCalcVariablesCount,
    affectedOutputsCount,
  }
}
